import React, { useMemo } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Empty } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { Chart, Axis, Geom, Tooltip, Coord } from 'bizcharts';

import { STATUS_LABELS, STATUS_CHART_COLORS } from '../../../configs/analytics.config';
import { space, radius, shadow, text, fontSize as fontSizeTokens } from '../../../configs/designTokens';
import useResponsiveTask from '../../../hooks/useResponsiveTask';

const cardStyle = {
  borderRadius: radius.lg,
  boxShadow: shadow.sm,
};

const headStyle = {
  fontSize: fontSizeTokens.subhead,
  fontWeight: 600,
  borderBottom: 'none',
};

const URGENCY_LABELS = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
  Unknown: 'Not Urgent',
};

const URGENCY_CHART_COLORS = {
  Low: '#22c55e',
  Medium: '#f59e0b',
  High: '#f97316',
  Critical: '#ef4444',
  'Not Urgent': '#94a3b8',
};

const groupBy = (tasks, field) => {
  const groups = {};
  tasks.forEach((task) => {
    const key = task[field] || 'Unknown';
    groups[key] = (groups[key] || 0) + 1;
  });
  return Object.entries(groups)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

const groupByAssignee = (tasks, assigneeFilter) => {
  const groups = {};
  const filterSet = assigneeFilter?.length ? new Set(assigneeFilter) : null;
  tasks.forEach((task) => {
    const assignees = [];
    if (task.assigneeUsers?.length) {
      task.assigneeUsers.forEach((u) => {
        if (!filterSet || filterSet.has(u.id)) {
          assignees.push(u.name || 'Unknown');
        }
      });
    } else if (task.assigneeUser) {
      if (!filterSet || filterSet.has(task.assigneeUser.id)) {
        assignees.push(task.assigneeUser.name || 'Unknown');
      }
    }
    if (assignees.length === 0 && !filterSet) {
      assignees.push('Unassigned');
    }
    assignees.forEach((name) => {
      groups[name] = (groups[name] || 0) + 1;
    });
  });
  return Object.entries(groups)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

const BreakdownTab = ({ filteredTasks = [], loading, rangeKey, assigneeFilter }) => {
  const { isMobile } = useResponsiveTask();

  const total = filteredTasks.length || 1;

  const statusData = useMemo(() =>
    groupBy(filteredTasks, 'status').map((item, idx) => ({
      key: item.name || idx,
      name: STATUS_LABELS[item.name] || item.name,
      rawStatus: item.name,
      count: item.count,
      percentage: Math.round((item.count / total) * 100),
    })),
    [filteredTasks, total]
  );

  const assigneeData = useMemo(() =>
    groupByAssignee(filteredTasks, assigneeFilter).map((item, idx) => ({
      key: item.name || idx,
      name: item.name,
      count: item.count,
      percentage: Math.round((item.count / total) * 100),
    })),
    [filteredTasks, total, assigneeFilter]
  );

  const requestTypeData = useMemo(() =>
    groupBy(filteredTasks, 'requestType').map((item, idx) => ({
      key: item.name || idx,
      name: item.name,
      count: item.count,
      percentage: Math.round((item.count / total) * 100),
    })),
    [filteredTasks, total]
  );

  const urgencyChartData = useMemo(() =>
    groupBy(filteredTasks, 'urgency').map((item) => ({
      name: URGENCY_LABELS[item.name] || item.name,
      count: item.count,
    })),
    [filteredTasks]
  );

  const statusColumns = [
    {
      title: 'Status',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Tag color={STATUS_CHART_COLORS[record.rawStatus] || '#3b82f6'}>{text}</Tag>
      ),
    },
    { title: 'Count', dataIndex: 'count', key: 'count', sorter: (a, b) => a.count - b.count },
    { title: '%', dataIndex: 'percentage', key: 'percentage', render: (v) => `${v}%` },
  ];

  const assigneeColumns = [
    { title: 'Assignee', dataIndex: 'name', key: 'name' },
    { title: 'Count', dataIndex: 'count', key: 'count', sorter: (a, b) => a.count - b.count },
    { title: '%', dataIndex: 'percentage', key: 'percentage', render: (v) => `${v}%` },
  ];

  const requestTypeColumns = [
    { title: 'Request Type', dataIndex: 'name', key: 'name' },
    { title: 'Count', dataIndex: 'count', key: 'count', sorter: (a, b) => a.count - b.count },
    { title: '%', dataIndex: 'percentage', key: 'percentage', render: (v) => `${v}%` },
  ];

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: space[4] }} align="top">
        <Col xs={24} lg={16}>
          <Card title="Tasks by Status" style={{ ...cardStyle, marginBottom: 16 }} headStyle={headStyle} loading={loading}>
            <Table
              dataSource={statusData}
              columns={statusColumns}
              pagination={false}
              size="small"
            />
          </Card>
          <Card title="Tasks by Request Type" style={cardStyle} headStyle={headStyle} loading={loading}>
            <Table
              dataSource={requestTypeData}
              columns={requestTypeColumns}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            style={{ ...cardStyle, marginBottom: 16 }}
            bodyStyle={{ padding: space[3] }}
          >
            <div style={{ fontSize: 12, color: text.secondary, marginBottom: space[1] }}>
              <FilterOutlined style={{ marginRight: 4 }} />
              {rangeKey || 'All Time'}
            </div>
            <Statistic
              value={filteredTasks.length}
              suffix="tasks"
              valueStyle={{ fontSize: 28, fontWeight: 600, color: '#3b82f6' }}
            />
          </Card>
          <Card title="Tasks by Urgency" style={cardStyle} bodyStyle={{ padding: '12px 16px' }} headStyle={headStyle} loading={loading}>
            {!loading && urgencyChartData.length === 0 ? (
              <Empty description="No data for selected period" />
            ) : (
              <Chart
                forceFit
                height={isMobile ? 160 : Math.max(160, urgencyChartData.length * 40)}
                data={urgencyChartData}
                padding={[10, 30, 20, isMobile ? 60 : 80]}
              >
                <Coord transpose />
                <Axis
                  name="name"
                  label={{ style: { fontSize: 12, fill: text.secondary } }}
                />
                <Axis
                  name="count"
                  label={{ style: { fontSize: 12, fill: text.secondary } }}
                />
                <Tooltip />
                <Geom
                  type="interval"
                  position="name*count"
                  color={['name', (val) => URGENCY_CHART_COLORS[val] || '#94a3b8']}
                  style={{ radius: [4, 4, 0, 0] }}
                />
              </Chart>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: space[4] }}>
        <Col xs={24} lg={16}>
          <Card title="Tasks by Assignee" style={cardStyle} headStyle={headStyle} loading={loading}>
            <Table
              dataSource={assigneeData}
              columns={assigneeColumns}
              pagination={{ pageSize: 10, size: 'small', showSizeChanger: false }}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default BreakdownTab;
